import { Currency } from '@mobi/utils/money'
import type { KambiBetOffer } from '@mobi/api-types/kambi'
import type { KambiSportsSelection, Selection } from '@mobi/betslip/types'
import {
  isKambiSportsMultiSelection,
  isKambiSportsSameGameMultiSelection,
  isKambiSportsSingleSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { formatKambiCurrencyToDollarDecimal } from '@core/Areas/KambiSports/helpers/currencyFormat'

export function getPriceFromKambiSportsSelection(selection: KambiSportsSelection): number {
  if (selection.betSelectionType === 'single') {
    const selectedOutcome = selection.betOffer.outcomes[0]
    return formatKambiCurrencyToDollarDecimal(selectedOutcome.odds)
  }

  if (selection.betSelectionType === 'same-game-multi') {
    return formatKambiCurrencyToDollarDecimal(selection.odds)
  }

  if (selection.betSelectionType === 'multi') {
    const oddsForLegs = selection.legs.map(leg =>
      isKambiSportsSingleSelection(leg) ? leg.betOffer.outcomes[0].odds : leg.odds
    )

    const calculatedMultiOdds = oddsForLegs.reduce(
      (acc, curr) => acc.multiply(formatKambiCurrencyToDollarDecimal(curr)),
      new Currency(1)
    )

    return calculatedMultiOdds.value
  }

  return 0
}

export function getSelectionNameFromKambiBetOffer(betOffer: KambiBetOffer): string {
  const outcome = betOffer.outcomes[0]
  return `${outcome.participant || outcome.label}${outcome.line ? ' ' + outcome.line / 1000 : ''}`
}

export function isKambiCashOutAvailable(selection: Selection | null) {
  return (
    (isKambiSportsSingleSelection(selection) && selection.betOffer.cashOutStatus === 'ENABLED') ||
    (isKambiSportsMultiSelection(selection) &&
      selection.legs.every(x =>
        isKambiSportsSingleSelection(x) ? x.betOffer.cashOutStatus === 'ENABLED' : false
      ))
  )
}

export function isKambiEarlySettlementAvailable(selection: Selection | null) {
  if (isKambiSportsSingleSelection(selection)) {
    return selection.betOffer.outcomes.every(
      x =>
        x.tags?.includes('ELIGIBLE_FOR_EARLY_SETTLEMENT') ||
        x.outcomeTags?.includes('ELIGIBLE_FOR_EARLY_SETTLEMENT')
    )
  }

  if (isKambiSportsSameGameMultiSelection(selection)) {
    return false
  }

  if (isKambiSportsMultiSelection(selection)) {
    return selection.legs.every(x => {
      if (isKambiSportsSameGameMultiSelection(x)) {
        return false
      } else if (isKambiSportsSingleSelection(x)) {
        return x.betOffer.outcomes.every(
          x =>
            x.tags?.includes('ELIGIBLE_FOR_EARLY_SETTLEMENT') ||
            x.outcomeTags?.includes('ELIGIBLE_FOR_EARLY_SETTLEMENT')
        )
      } else {
        return false
      }
    })
  }
  return false
}
